import { useRef } from "react";
import {
      motion,
      useMotionValue,
      useSpring,
      useTransform,
      useReducedMotion,
} from "framer-motion";
import { Reveal } from "./motion";
import { InViewTextEffect } from "@/components/ui/text-effect";
import RouteWaypoints, { getRoutePathPct } from "./RouteWaypoints";
import RouteHiker from "./RouteHiker";

const ROUTE_POINTS = [[0.04, 0.72], [0.35, 0.5], [0.65, 0.22], [0.96, 0.34]];
const ROUTE_PATH = getRoutePathPct(ROUTE_POINTS);

const STEPS = [
    {
            code: "01",
            tag: "RECON",
            title: "Diagnose",
            body: "We map your current site, competitors, and customers — what's working, what's leaking, what's missing. No build starts without a clear objective.",
    },
    {
            code: "02",
            tag: "ROUTE",
            title: "Build",
            body: "I plot the route and construct the site — clean, fast, mobile-first, hand-coded. You see progress as it happens, not a black box.",
    },
    {
            code: "03",
            tag: "SUMMIT",
            title: "Launch",
            body: "We go live together with a walkthrough and a control-boundary map, so you know exactly what you can change and what stays locked.",
    },
    {
            code: "04",
            tag: "MAINTAIN",
            title: "Sustain",
            body: "Optional monthly upkeep — hosting, security, uptime monitoring, and small content changes. The site stays sharp long after summit.",
    },
    ];

function TiltCard({ step, index }) {
      const reduce = useReducedMotion();
      const ref = useRef(null);
      const mx = useMotionValue(0);
      const my = useMotionValue(0);
      const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), {
              stiffness: 200,
              damping: 18,
      });
      const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), {
              stiffness: 200,
              damping: 18,
      });

  const onMove = (e) => {
          if (reduce || !ref.current) return;
          const r = ref.current.getBoundingClientRect();
          mx.set((e.clientX - r.left) / r.width - 0.5);
          my.set((e.clientY - r.top) / r.height - 0.5);
  };
      const onLeave = () => {
              mx.set(0);
              my.set(0);
      };

  return (
          <motion.div
                    className="w-full"
                    style={{ perspective: 900 }}
                    initial={reduce ? { opacity: 0 } : { opacity: 0, y: 28 }}
                    whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: index * 0.09, ease: [0.16, 1, 0.3, 1] }}
                  >
                <motion.article
                            ref={ref}
                            onMouseMove={onMove}
                            onMouseLeave={onLeave}
                            style={{ rotateX: reduce ? 0 : rx, rotateY: reduce ? 0 : ry, transformStyle: "preserve-3d" }}
                            data-testid={`process-card-${step.code}`}
                            className="group relative h-full border border-hairline bg-panel/60 p-6 transition-colors duration-300 hover:border-ink/30"
                          >
                        <div className="flex items-baseline justify-between" style={{ transform: "translateZ(40px)" }}>
                                  <span className="font-serif text-4xl leading-none text-ink/25 transition-colors duration-300 group-hover:text-pine">
                                      {step.code}
                                  </span>
                                  <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.24em] text-rust-deep">
                                              · {step.tag}
                                  </span>
                        </div>
                        <h3
                                      className="mt-6 font-serif text-2xl text-ink"
                                      style={{ transform: "translateZ(30px)" }}
                                    >
                            {step.title}
                        </h3>
                        <p
                                      className="mt-2.5 text-[13px] leading-relaxed text-ink-dim"
                                      style={{ transform: "translateZ(20px)" }}
                                    >
                            {step.body}
                        </p>
                </motion.article>
          </motion.div>
        );
}

export default function Process({ hikerVisible = false }) {
      const sectionRef = useRef(null);
    
      return (
              <section
                        ref={sectionRef}
                        id="process"
                        className="relative z-10 overflow-hidden border-t border-hairline px-5 py-24 md:px-8 md:py-32"
                        data-testid="process-section"
                      >
                    <div className="mx-auto max-w-7xl">
                            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                                      <div>
                                                  <InViewTextEffect
                                                                as="p"
                                                                preset="blur"
                                                                className="font-mono text-[11px] uppercase tracking-[0.24em] text-rust"
                                                              >
                                                                CH.02 // Field Manual
                                                  </InViewTextEffect>
                                                  <Reveal
                                                                    as="h2"
                                                                    delay={0.05}
                                                                    className="mt-5 max-w-lg font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl"
                                                                  >
                                                                Four stages. One route to the <em className="italic text-pine">summit</em>.
                                                  </Reveal>
                                      </div>
                                      <Reveal delay={0.1} className="max-w-xs text-sm leading-relaxed text-ink-dim">
                                                  Every build follows the same disciplined path — borrowed from mission
                                                  planning, adapted for the web.
                                      </Reveal>
                            </div>
                    </div>

                  {/* one continuous route through all four stages — connective tissue,
                      not a disconnected card grid (ZD-05) */}
                    <div className="relative mx-auto mt-14 hidden max-w-7xl lg:block">
                            <RouteWaypoints
                                      viewBox="0 0 800 90"
                                      points={ROUTE_POINTS}
                                      waypoints={STEPS.map((s, i) => ({ at: i, label: s.tag }))}
                                    />
                            {hikerVisible && <RouteHiker path={ROUTE_PATH} size={28} speed={46} />}
                    </div>

                  {/* static positioning, but each card animates in with a stagger as it enters view */}
                    <div className="relative mx-auto mt-10 max-w-7xl lg:mt-6">
                            {/* below lg: a straight vertical rail + evenly-spaced waypoints stand in
                                for the horizontal spline above, so the route stays continuous
                                instead of collapsing into disconnected cards (ZD-05) */}
                            <div
                                      className="absolute inset-y-1 left-[15px] w-px bg-rust/30 lg:hidden"
                                      aria-hidden="true"
                                    />
                            <div
                                      className="absolute inset-y-1 left-[15px] flex w-2.5 -translate-x-1/2 flex-col items-center justify-between lg:hidden"
                                      aria-hidden="true"
                                    >
                                {STEPS.map((s) => (
                                      <span key={s.code} className="h-2.5 w-2.5 flex-none rounded-full bg-rust ring-4 ring-rust/15" />
                                    ))}
                            </div>

                            <div className="grid grid-cols-1 gap-6 pl-10 lg:grid-cols-4 lg:pl-0">
                                {STEPS.map((s, i) => (
                                      <TiltCard key={s.code} step={s} index={i} />
                                    ))}
                            </div>
                    </div>
              </section>
            );
}
